import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRejectInvoices } from "../../../graphql/hooks/invoices/useRejectInvoices";
import { useSendDocumentRejectionEmail } from "../../../graphql/hooks/documentHandler/useSendDocumentRejectionEmail";
import { validateEmail } from "../../../utils/validation";
import RejectInvoicesModal from "../assessment/RejectInvoicesModal";

type Options =
  | {
      type: "single";
      invoiceId: string;
      fileId?: string;
      onSuccess?: () => void;
    }
  | { type: "multiple"; invoiceIds: string[]; onSuccess?: () => void };

export const useRejectInvoicesFlow = (options: Options) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [sendRejectionEmail, setSendRejectionEmail] = useState(false);
  const [clientRecipientEmail, setClientRecipientEmail] = useState("");
  const [clientMessage, setClientMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | undefined>(
    undefined,
  );
  const [attachFile, setAttachFile] = useState(false);
  // The modal keeps the user's draft while it is closed, so reopening it
  // restores what they had written. Bumping this key remounts the modal and is
  // therefore how the draft gets cleared once the rejection has gone through.
  const [formKey, setFormKey] = useState(0);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const resetAfterSuccess = () => {
    setSuccessMessage(undefined);
    setErrorMessage(undefined);
    setSendRejectionEmail(false);
    setClientRecipientEmail("");
    setClientMessage("");
    setAttachFile(false);
    setFormKey((key) => key + 1);
  };

  const onRejectionCompleted = () => {
    setSuccessMessage(t("invoices.rejectInvoiceSuccess"));
    options.onSuccess?.();
    closeTimeout.current = setTimeout(() => {
      setModalOpen(false);
      resetAfterSuccess();
    }, 5000);
  };

  const openModal = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = undefined;
    }
    // Reopened before the success message timed out: start from a clean form
    // instead of the already rejected draft.
    if (successMessage) {
      resetAfterSuccess();
    } else {
      setErrorMessage(undefined);
    }
    setModalOpen(true);
  };

  const onCompletedRejectInvoice = (res?: any) => {
    console.log("Reject invoice result", res);
    if (sendRejectionEmail) {
      onSendRejectionEmailToClient();
    } else {
      onRejectionCompleted();
    }
  };

  const onErrorRejectInvoice = (e?: any) => {
    console.error("Error rejecting invoice", e);
    setErrorMessage(t("invoices.rejectInvoiceError"));
  };

  const {
    rejectInvoices,
    loading: rejectInvoiceLoading,
    error: rejectInvoiceError,
  } = useRejectInvoices({
    onCompleted: onCompletedRejectInvoice,
    onError: onErrorRejectInvoice,
  });

  const onClickReject = async (
    comment?: string,
    recipientEmail?: string,
    emailMessage?: string,
    sendFileAsAttachment?: boolean,
  ) => {
    setErrorMessage(undefined);
    setSendRejectionEmail(false);
    setAttachFile(false);
    if (!comment) {
      setErrorMessage(t("invoices.rejectInvoiceCommentRequired"));
      return;
    }
    if (recipientEmail && !validateEmail(recipientEmail)) {
      setErrorMessage(t("documentHandler.invalidClientEmail"));
      return;
    }
    if (
      (recipientEmail && !emailMessage) ||
      (!recipientEmail && emailMessage)
    ) {
      setErrorMessage(t("documentHandler.clientEmailAndMessageRequired"));
      return;
    }
    if (recipientEmail && emailMessage) {
      setSendRejectionEmail(true);
      setClientRecipientEmail(recipientEmail);
      setClientMessage(emailMessage);
      if (sendFileAsAttachment) setAttachFile(true);
    }

    try {
      await rejectInvoices({
        variables: {
          invoices:
            options.type === "single"
              ? [{ invoiceId: options.invoiceId, reason: comment }]
              : options.invoiceIds.map((id) => ({
                  invoiceId: id,
                  reason: comment,
                })),
        },
      });
    } catch (e) {
      console.error("Error rejecting invoice(s)", e);
      setErrorMessage(t("invoices.rejectInvoiceError"));
    }
  };

  const onCompletedSendRejectionEmail = (data: any) => {
    if (data?.documentHandler?.sendDocumentRejectionEmail?.success === true) {
      onRejectionCompleted();
    } else {
      setErrorMessage(t("documentHandler.sendRejectionEmailFailed"));
    }
  };

  const onErrorSendRejectionEmail = (error: any) => {
    console.error("Error sending rejection email to client:", error);
    setErrorMessage(t("documentHandler.sendRejectionEmailFailed"));
  };

  const { sendDocumentRejectionEmail } = useSendDocumentRejectionEmail({
    onCompleted: onCompletedSendRejectionEmail,
    onError: onErrorSendRejectionEmail,
  });

  const onSendRejectionEmailToClient = async () => {
    if (
      !clientRecipientEmail ||
      !clientMessage ||
      !validateEmail(clientRecipientEmail)
    ) {
      setErrorMessage(t("documentHandler.invalidClientEmailOrMessage"));
      return;
    }
    try {
      await sendDocumentRejectionEmail({
        variables: {
          input: {
            emailRecipient: clientRecipientEmail,
            rejectionReason: clientMessage,
            fileId:
              attachFile && options.type === "single"
                ? options.fileId
                : undefined,
          },
        },
      });
    } catch (e) {
      console.error("Error sending rejection email to client:", e);
      setErrorMessage(e?.toString() ?? t("errors.somethingWentWrong"));
    }
  };

  const modal = (
    <RejectInvoicesModal
      key={formKey}
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      onReject={(params) =>
        onClickReject(
          params.comment,
          params.recipientEmail,
          params.emailMessage,
          params.sendFileAsAttachment,
        )
      }
      count={options.type === "single" ? 1 : options.invoiceIds.length}
      loading={rejectInvoiceLoading}
      errorMessage={errorMessage ?? rejectInvoiceError?.toString()}
      successMessage={successMessage}
      canSendFileAsAttachment={options.type === "single" && !!options.fileId}
    />
  );

  return { openModal, modal };
};
